import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    ComposedChart, Bar, Line, Area, Scatter, ZAxis, LabelList, ReferenceLine,
    PieChart, Pie, Cell,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { toPng } from 'html-to-image';
import png from '../../images/icons/downloadpng.svg';
import {
    buildChartModel, formatValue, formatWithUnit, MUTED_COLOR, MAX_LEGEND_ITEMS,
} from '../../business/helper/ChartModel';

/**
 * Draws one chart of the AI's chart contract. Everything about *what* to draw — the series,
 * the order of the categories, the range and ticks of every axis, which series are muted,
 * whether a radar is really a bar chart — is decided by `buildChartModel`; this file only
 * draws the model with Recharts in the app's palette and typography.
 */

const isMac = typeof navigator !== 'undefined' && (
    navigator.platform.toUpperCase().includes('MAC') || navigator.userAgent.includes('Mac'));

const titleFont = isMac ? '"Helvetica Neue", Arial, sans-serif' : '"Segoe UI", sans-serif';

const FONT_FAMILY = isMac
    ? '"Helvetica Neue", Arial, sans-serif'
    : '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

const INK = {
    axis: '#8C8C8C',
    grid: '#ECEEF2',
    axisLine: '#E0E0E0',
    label: '#6B7280',
    text: '#374151',
    muted: '#8C8C8C',
};

const CHAR_PX = 6.6;          // average glyph width of the 12px axis font
const VALUE_AXIS_MIN = 44;    // narrowest value axis

const truncate = (text, max) => {
    const value = String(text ?? '');
    return value.length > max ? `${value.slice(0, Math.max(1, max - 1))}…` : value;
};

/** The rendered width of the wrapper, so tick density can be decided from real pixels. */
const useContainerWidth = (ref, fallback = 760) => {
    const [width, setWidth] = useState(fallback);
    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        const measure = () => {
            const next = node.getBoundingClientRect().width;
            if (next > 0) setWidth(next);
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [ref]);
    return width;
};

/* ------------------------------------------------------------------------------- pieces */

const CategoryTick = ({
    x, y, payload, angle = 0, maxChars = 24, vertical = false, edgeAnchors = false, index, visibleTicksCount,
}) => {
    const text = String(payload?.value ?? '');
    const shown = truncate(text, maxChars);
    const common = { fontFamily: FONT_FAMILY, fontSize: 12, fill: INK.axis };
    // On a point scale the first and last ticks sit on the plot's edges: anchor their labels
    // inwards so a long label stays inside the card.
    let anchor = 'middle';
    if (edgeAnchors && visibleTicksCount > 1) {
        if (index === 0) anchor = 'start';
        else if (index === visibleTicksCount - 1) anchor = 'end';
    }
    if (vertical) {
        return (
            <text x={x - 6} y={y} dy={4} textAnchor="end" style={common}>
                <title>{text}</title>{shown}
            </text>
        );
    }
    if (angle) {
        return (
            <text x={x} y={y} dy={6} textAnchor="end" transform={`rotate(${angle}, ${x}, ${y})`} style={common}>
                <title>{text}</title>{shown}
            </text>
        );
    }
    return (
        <text x={x} y={y} dy={14} textAnchor={anchor} style={common}>
            <title>{text}</title>{shown}
        </text>
    );
};

const RingDot = ({ cx, cy, fill, r = 3.5 }) => {
    // A gap in the series reaches here with no coordinates: no dot, no NaN in the SVG.
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
    return <circle cx={cx} cy={cy} r={r} fill={fill} stroke="#fff" strokeWidth={1.5} />;
};

const Swatch = ({ series, color }) => {
    const fill = color ?? series?.color;
    if (series?.type === 'line' || series?.type === 'scatter') {
        return (
            <span className="relative inline-block mr-2" style={{ width: 16, height: 3, backgroundColor: series.type === 'line' ? fill : 'transparent', borderRadius: 2 }}>
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 8, height: 8, backgroundColor: fill }} />
            </span>
        );
    }
    return (
        <span className="inline-block mr-2 rounded-[3px]" style={{ width: 10, height: 10, backgroundColor: fill, opacity: series?.type === 'area' ? 0.6 : 1 }} />
    );
};

const LegendRow = ({ items, className = '' }) => (
    <div className={`flex flex-wrap items-center gap-x-5 gap-y-1.5 ${className}`} data-chart-legend="true">
        {items.map((item) => (
            <div key={item.key} className="flex items-center" title={item.title}>
                <Swatch series={item.series} color={item.color} />
                <span style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: INK.text, fontWeight: 500 }}>
                    {item.label}
                </span>
            </div>
        ))}
    </div>
);

const TooltipFrame = ({ title, rows, footer }) => (
    <div style={{
        backgroundColor: '#fff',
        padding: '10px 12px',
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        boxShadow: '0 4px 14px rgba(17, 24, 39, 0.10)',
        fontFamily: FONT_FAMILY,
        maxWidth: 360,
    }}>
        {title && <p style={{ margin: '0 0 6px 0', fontSize: 12, color: INK.muted, fontWeight: 600 }}>{title}</p>}
        {rows.map((row) => (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: INK.text, lineHeight: '20px' }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, backgroundColor: row.color, flex: '0 0 auto' }} />
                <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</span>
                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{row.value}</span>
            </div>
        ))}
        {footer && (
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #F0F0F0', fontSize: 12, color: INK.muted, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>{footer.name}</span><span style={{ fontWeight: 700, color: INK.text }}>{footer.value}</span>
            </div>
        )}
    </div>
);

/* ---------------------------------------------------------------------- cartesian panel */

const CartesianPanel = ({ panel, width }) => {
    const { rows, series, x, leftAxis, rightAxis, horizontal } = panel;
    const byId = useMemo(() => new Map(series.map((s) => [s.id, s])), [series]);
    const unitOf = (s) => (s.rightAxis && rightAxis ? rightAxis.unit : leftAxis.unit);

    // Axis geometry from the real width: how wide each value axis is, whether the category
    // labels fit straight, rotated, or only every few.
    const leftTicks = leftAxis.scale.ticks.map(leftAxis.scale.format);
    const leftWidth = Math.max(VALUE_AXIS_MIN, Math.max(...leftTicks.map((t) => t.length)) * CHAR_PX + 14);
    const rightTicks = rightAxis ? rightAxis.scale.ticks.map(rightAxis.scale.format) : [];
    const rightWidth = rightAxis ? Math.max(VALUE_AXIS_MIN, Math.max(...rightTicks.map((t) => t.length)) * CHAR_PX + 14) : 0;

    let categoryWidth = 0;
    let tickAngle = 0;
    let maxChars = 24;
    let interval = 0;
    let xHeight = 30;
    if (horizontal) {
        const longest = Math.min(34, panel.longestLabel);
        categoryWidth = Math.min(Math.round(width * 0.44), Math.max(72, longest * CHAR_PX + 14));
        maxChars = Math.max(6, Math.floor((categoryWidth - 14) / CHAR_PX));
    } else if (x.kind !== 'number') {
        const plot = Math.max(120, width - leftWidth - rightWidth - 24);
        const slot = plot / Math.max(1, rows.length);
        const labelPx = panel.longestLabel * CHAR_PX;
        if (labelPx <= slot - 6) {
            tickAngle = 0;
            maxChars = Math.max(6, Math.floor((slot - 6) / CHAR_PX));
        } else if (slot >= 13) {
            tickAngle = -35;
            maxChars = 22;
            const shownPx = Math.min(panel.longestLabel, maxChars) * CHAR_PX;
            xHeight = Math.min(120, Math.round(14 + shownPx * Math.sin((35 * Math.PI) / 180)));
        } else {
            tickAngle = -35;
            maxChars = 16;
            interval = Math.ceil(13 / slot) - 1;
            xHeight = Math.min(110, Math.round(14 + Math.min(panel.longestLabel, maxChars) * CHAR_PX * 0.6));
        }
    }

    const valueTick = { fontFamily: FONT_FAMILY, fontSize: 12, fill: INK.axis };
    const valueAxisProps = (axis) => ({
        domain: [axis.scale.min, axis.scale.max],
        ticks: axis.scale.ticks,
        tickFormatter: axis.scale.format,
        tick: valueTick,
        axisLine: false,
        tickLine: false,
        allowDataOverflow: true,
    });

    const categoryAxisProps = {
        dataKey: x.kind === 'number' ? 'x' : 'label',
        type: x.kind === 'number' ? 'number' : 'category',
        ...(x.kind === 'number' && panel.xScale ? {
            domain: [panel.xScale.min, panel.xScale.max],
            ticks: panel.xScale.ticks,
            tickFormatter: panel.xScale.format,
        } : {}),
        axisLine: { stroke: INK.axisLine },
        tickLine: false,
    };

    const renderTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const seen = new Set();
        const tooltipRows = [];
        let stackTotal = null;
        payload.forEach((entry) => {
            const s = byId.get(entry.dataKey);
            if (!s || seen.has(s.id)) return;
            const value = entry.payload?.[s.id];
            if (value === null || value === undefined) return;
            seen.add(s.id);
            const color = entry.payload?.[`${s.id}_color`] || (s.muted ? MUTED_COLOR : s.color);
            const z = entry.payload?.[`${s.id}_z`];
            tooltipRows.push({
                key: s.id,
                color,
                name: s.title,
                value: z !== undefined && z !== null ? `${formatWithUnit(value, unitOf(s))} · ${formatValue(z)}` : formatWithUnit(value, unitOf(s)),
            });
            if (s.stackId && s.type === 'bar') stackTotal = (stackTotal ?? 0) + value;
        });
        if (!tooltipRows.length) return null;
        const title = x.kind === 'number' ? formatValue(Number(label)) : String(payload[0]?.payload?.key ?? label);
        const footer = stackTotal !== null && tooltipRows.length > 1 ? { name: 'Total', value: formatWithUnit(stackTotal, leftAxis.unit) } : null;
        return <TooltipFrame title={title} rows={tooltipRows} footer={footer} />;
    };

    const stackTops = useMemo(() => {
        const tops = new Map();
        series.forEach((s) => { if (s.stackId) tops.set(s.stackId, s.id); });
        return tops;
    }, [series]);

    const barRadius = (s) => {
        if (s.stackId && !s.disjoint && stackTops.get(s.stackId) !== s.id) return 0;
        return horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0];
    };

    const scatterSeries = series.filter((s) => s.type === 'scatter');
    const legendItems = series.filter((s) => !s.muted).map((s) => ({ key: s.id, label: s.title, title: s.title, series: s }));
    const mutedCount = series.filter((s) => s.muted).length;
    if (mutedCount) legendItems.push({ key: '_muted', label: `${mutedCount} more`, title: series.filter((s) => s.muted).map((s) => s.title).join(', '), series: { type: 'line', color: MUTED_COLOR } });

    const topCaption = horizontal ? panel.xTitle : leftAxis.title;
    const bottomCaption = horizontal ? leftAxis.title : panel.xTitle;
    // Bar value labels keep a symbol unit (%, £); a word unit stays in the tooltip.
    const labelUnit = (s) => {
        const unit = unitOf(s);
        return unit && /^(%|[£$€¥])$/.test(unit) ? unit : null;
    };
    // On a point scale (no bars) the first and last ticks sit on the plot edges; a label wider
    // than the room beside the plot is anchored inwards rather than centred over the edge.
    const edgeAnchors = !horizontal && tickAngle === 0 && x.kind !== 'number'
        && !series.some((s) => s.type === 'bar')
        && (Math.min(panel.longestLabel, maxChars) * CHAR_PX) / 2 > leftWidth + 4;
    // A rotated first label reaches left of its tick; give it room instead of letting it leave the card.
    const rotatedReach = tickAngle
        ? Math.min(panel.longestLabel, maxChars) * CHAR_PX * Math.cos((35 * Math.PI) / 180)
        : 0;
    const margin = {
        top: 18,
        right: rightAxis ? 8 : (horizontal && panel.valueLabels ? 56 : 18),
        left: Math.max(4, Math.round(rotatedReach - leftWidth)),
        bottom: 4,
    };

    return (
        <div>
            {(topCaption || (rightAxis && rightAxis.title)) && (
                <div className="flex justify-between gap-4 mb-1" style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: INK.muted }}>
                    <span>{topCaption}</span>
                    {rightAxis?.title && <span className="text-right">{rightAxis.title} →</span>}
                </div>
            )}
            <ResponsiveContainer width="100%" height={panel.height}>
                <ComposedChart data={rows} layout={horizontal ? 'vertical' : 'horizontal'} margin={margin} barCategoryGap="22%" barGap={2}>
                    <CartesianGrid stroke={INK.grid} vertical={horizontal} horizontal={!horizontal} />
                    {horizontal ? (
                        <>
                            <XAxis type="number" {...valueAxisProps(leftAxis)} axisLine={{ stroke: INK.axisLine }} height={28} />
                            <YAxis
                                {...categoryAxisProps}
                                width={categoryWidth}
                                interval={0}
                                tick={<CategoryTick vertical maxChars={maxChars} />}
                            />
                        </>
                    ) : (
                        <>
                            <XAxis
                                {...categoryAxisProps}
                                interval={x.kind === 'number' ? undefined : interval}
                                height={xHeight}
                                minTickGap={x.kind === 'number' ? 24 : undefined}
                                tick={x.kind === 'number'
                                    ? { fontFamily: FONT_FAMILY, fontSize: 12, fill: INK.axis }
                                    : <CategoryTick angle={tickAngle} maxChars={maxChars} edgeAnchors={edgeAnchors} />}
                            />
                            <YAxis yAxisId="left" width={leftWidth} {...valueAxisProps(leftAxis)} />
                            {rightAxis && (
                                <YAxis yAxisId="right" orientation="right" width={rightWidth} {...valueAxisProps(rightAxis)} />
                            )}
                        </>
                    )}
                    {scatterSeries.map((s) => (
                        <ZAxis key={`z-${s.id}`} zAxisId={s.id} dataKey={`${s.id}_z`} range={[50, 420]} />
                    ))}
                    {leftAxis.scale.min < 0 && (
                        <ReferenceLine {...(horizontal ? { x: 0 } : { y: 0, yAxisId: 'left' })} stroke="#9CA3AF" />
                    )}
                    <Tooltip content={renderTooltip} cursor={{ fill: 'rgba(94, 98, 204, 0.06)', stroke: INK.axisLine }} />
                    {series.map((s) => {
                        const color = s.muted ? MUTED_COLOR : s.color;
                        const axisId = horizontal ? undefined : (s.rightAxis && rightAxis ? 'right' : 'left');
                        if (s.type === 'bar') {
                            return (
                                <Bar
                                    key={s.id}
                                    dataKey={s.id}
                                    name={s.title}
                                    yAxisId={axisId}
                                    stackId={s.stackId}
                                    fill={color}
                                    maxBarSize={horizontal ? 26 : 36}
                                    radius={barRadius(s)}
                                    stroke={s.stackId ? '#fff' : undefined}
                                    strokeWidth={s.stackId ? 1 : 0}
                                    isAnimationActive={false}
                                >
                                    {s.hasPointColors && rows.map((row) => (
                                        <Cell key={row.key} fill={row[`${s.id}_color`] || color} />
                                    ))}
                                    {panel.valueLabels && (
                                        <LabelList
                                            dataKey={s.id}
                                            position={horizontal ? 'right' : 'top'}
                                            formatter={(v) => formatWithUnit(v, labelUnit(s))}
                                            style={{ fontFamily: FONT_FAMILY, fontSize: 11, fill: INK.label }}
                                        />
                                    )}
                                </Bar>
                            );
                        }
                        if (s.type === 'area') {
                            return (
                                <Area
                                    key={s.id}
                                    type="monotone"
                                    dataKey={s.id}
                                    name={s.title}
                                    yAxisId={axisId}
                                    stackId={s.stackId}
                                    stroke={color}
                                    strokeWidth={s.muted ? 1.25 : 2}
                                    strokeDasharray={s.dashed ? '6 4' : undefined}
                                    fill={color}
                                    fillOpacity={s.muted ? 0.05 : 0.12}
                                    dot={!s.noDots || rows.length <= 2 ? <RingDot fill={color} /> : false}
                                    activeDot={{ r: 5, stroke: '#fff', strokeWidth: 1.5 }}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                />
                            );
                        }
                        if (s.type === 'scatter') {
                            return (
                                <Scatter
                                    key={s.id}
                                    dataKey={s.id}
                                    name={s.title}
                                    yAxisId={axisId}
                                    zAxisId={s.id}
                                    fill={color}
                                    shape={(props) => <RingDot cx={props.cx} cy={props.cy} fill={props.payload?.[`${s.id}_color`] || color} r={Math.max(4.5, Math.sqrt((props.size || 60) / Math.PI))} />}
                                    isAnimationActive={false}
                                />
                            );
                        }
                        return (
                            <Line
                                key={s.id}
                                type="monotone"
                                dataKey={s.id}
                                name={s.title}
                                yAxisId={axisId}
                                stroke={color}
                                strokeWidth={s.muted ? 1.25 : 2}
                                strokeDasharray={s.dashed ? '6 4' : undefined}
                                dot={s.muted ? false : (!s.noDots || rows.length <= 2 ? <RingDot fill={color} /> : false)}
                                activeDot={{ r: 5, stroke: '#fff', strokeWidth: 1.5 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        );
                    })}
                </ComposedChart>
            </ResponsiveContainer>
            {bottomCaption && (
                <div className="text-center mt-1" style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: INK.muted }}>{bottomCaption}</div>
            )}
            {panel.legend && <LegendRow items={legendItems} className="mt-3" />}
        </div>
    );
};

/* ---------------------------------------------------------------------------- pie panel */

const RADIAN = Math.PI / 180;

const sliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.06) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" style={{ fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: 600 }}>
            {`${Math.round(percent * 100)}%`}
        </text>
    );
};

const Donut = ({ pie, size, showLabels = true }) => {
    const renderTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const slice = payload[0].payload;
        return (
            <TooltipFrame
                title={pie.title}
                rows={[{ key: slice.name, color: slice.color, name: slice.name, value: `${formatValue(slice.value)} · ${Math.round(slice.share * 100)}%` }]}
            />
        );
    };
    if (pie.empty) {
        return (
            <div className="flex items-center justify-center rounded-full border border-dashed mx-auto" style={{ width: size * 0.7, height: size * 0.7, borderColor: '#D1D5DB', color: INK.muted, fontFamily: FONT_FAMILY, fontSize: 13 }}>
                No data
            </div>
        );
    }
    return (
        <div style={{ position: 'relative', width: '100%', height: size }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={pie.slices}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius="58%"
                        outerRadius="88%"
                        paddingAngle={pie.slices.length > 1 ? 1.5 : 0}
                        labelLine={false}
                        label={showLabels ? sliceLabel : false}
                        stroke="#fff"
                        strokeWidth={1}
                        isAnimationActive={false}
                    >
                        {pie.slices.map((slice) => <Cell key={slice.name} fill={slice.color} />)}
                    </Pie>
                    <Tooltip content={renderTooltip} />
                </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ fontFamily: FONT_FAMILY }}>
                <span style={{ fontSize: size > 260 ? 22 : 17, fontWeight: 700, color: INK.text }}>{formatValue(pie.total)}</span>
                <span style={{ fontSize: 11, color: INK.muted }}>Total</span>
            </div>
        </div>
    );
};

const SliceList = ({ slices, compact = false }) => (
    <div className={`flex flex-col ${compact ? 'gap-1' : 'gap-1.5'} overflow-y-auto`} style={{ maxHeight: compact ? 160 : 300, fontFamily: FONT_FAMILY }}>
        {slices.map((slice) => (
            <div key={slice.name} className="flex items-center gap-2" title={slice.name}>
                <span className="inline-block rounded-full flex-none" style={{ width: 10, height: 10, backgroundColor: slice.color }} />
                <span className="truncate" style={{ fontSize: compact ? 12 : 13, color: INK.text, flex: '1 1 auto', minWidth: 0 }}>{slice.name}</span>
                <span style={{ fontSize: compact ? 12 : 13, color: INK.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatValue(slice.value)}
                    <span style={{ color: INK.muted, fontWeight: 400 }}> · {Math.round(slice.share * 100)}%</span>
                </span>
            </div>
        ))}
    </div>
);

const PiePanel = ({ panel }) => {
    const { pies, sharedCategories } = panel;
    if (pies.length === 1) {
        const pie = pies[0];
        return (
            <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-full md:w-[46%] flex-none"><Donut pie={pie} size={panel.height} /></div>
                <div className="w-full md:flex-1 min-w-0">
                    {pie.title && pie.title !== 'Series 1' && (
                        <div className="mb-2" style={{ fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: 600, color: INK.text }}>{pie.title}</div>
                    )}
                    {pie.empty ? (
                        <div style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: INK.muted }}>Every value is zero or missing.</div>
                    ) : pie.legend ? <SliceList slices={pie.slices} /> : (
                        <div style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: INK.muted }}>{pie.slices.length} slices · hover a slice for its name and value</div>
                    )}
                </div>
            </div>
        );
    }
    const legendSlices = sharedCategories ? pies.find((p) => !p.empty)?.slices ?? [] : null;
    return (
        <div>
            <div className="grid gap-5" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${pies.length > 3 ? 180 : 220}px, 1fr))` }}>
                {pies.map((pie) => (
                    <div key={pie.id} className="flex flex-col items-center min-w-0">
                        <div className="mb-1 text-center truncate w-full" title={pie.title} style={{ fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: 600, color: INK.text }}>{pie.title}</div>
                        <div className="w-full"><Donut pie={pie} size={pies.length > 3 ? 190 : 230} /></div>
                        {!sharedCategories && !pie.empty && pie.legend && <div className="w-full mt-2"><SliceList slices={pie.slices} compact /></div>}
                    </div>
                ))}
            </div>
            {legendSlices && legendSlices.length > 0 && legendSlices.length <= MAX_LEGEND_ITEMS && (
                <LegendRow className="mt-4" items={legendSlices.map((slice) => ({ key: slice.name, label: slice.name, title: slice.name, series: { type: 'bar' }, color: slice.color }))} />
            )}
        </div>
    );
};

/* -------------------------------------------------------------------------- radar panel */

const RadarPanel = ({ panel }) => {
    const { rows, series, scale } = panel;
    const byId = new Map(series.map((s) => [s.id, s]));
    const renderTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const tooltipRows = payload
            .filter((entry) => entry.value !== null && entry.value !== undefined)
            .map((entry) => ({ key: entry.dataKey, color: byId.get(entry.dataKey)?.color, name: byId.get(entry.dataKey)?.title, value: formatWithUnit(entry.value, panel.unit) }));
        return <TooltipFrame title={String(label)} rows={tooltipRows} />;
    };
    return (
        <div>
            <ResponsiveContainer width="100%" height={panel.height}>
                <RadarChart data={rows} cx="50%" cy="50%" outerRadius="72%">
                    <PolarGrid stroke={INK.grid} />
                    <PolarAngleAxis dataKey="label" tick={panel.axisLabels ? { fontFamily: FONT_FAMILY, fontSize: 12, fill: INK.axis } : false} tickFormatter={(v) => truncate(v, 22)} />
                    <PolarRadiusAxis angle={90} domain={[scale.min, scale.max]} ticks={scale.ticks} tickFormatter={scale.format} tick={{ fontFamily: FONT_FAMILY, fontSize: 10, fill: '#A3A3A3' }} axisLine={false} />
                    <Tooltip content={renderTooltip} />
                    {series.map((s) => (
                        <Radar
                            key={s.id}
                            dataKey={s.id}
                            name={s.title}
                            stroke={s.color}
                            strokeWidth={2}
                            fill={s.color}
                            fillOpacity={0.16}
                            dot={<RingDot fill={s.color} r={3} />}
                            isAnimationActive={false}
                        />
                    ))}
                </RadarChart>
            </ResponsiveContainer>
            {panel.legend && <LegendRow className="mt-2 justify-center" items={series.map((s) => ({ key: s.id, label: s.title, title: s.title, series: { type: 'area', color: s.color } }))} />}
        </div>
    );
};

/* ------------------------------------------------------------------------------- the card */

const Panel = ({ panel, width }) => {
    let body;
    if (panel.kind === 'pie') body = <PiePanel panel={panel} />;
    else if (panel.kind === 'radar') body = <RadarPanel panel={panel} />;
    else body = <CartesianPanel panel={panel} width={width} />;
    return (
        <div>
            {panel.heading && (
                <div className="mb-2" style={{ fontFamily: FONT_FAMILY, fontSize: 14, fontWeight: 600, color: INK.text }}>{panel.heading}</div>
            )}
            {body}
            {panel.notes?.map((note) => (
                <p key={note} className="mt-2" style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: INK.muted, fontStyle: 'italic' }}>{note}</p>
            ))}
        </div>
    );
};

const DynamicRechartsChart = ({ chartConfig }) => {
    const chartContainerRef = useRef(null);
    const bodyRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);
    const width = useContainerWidth(bodyRef);
    const model = useMemo(() => buildChartModel(chartConfig), [chartConfig]);

    const sanitizeFilename = (title) =>
        (title || 'chart')
            .toLowerCase()
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '') || 'chart';

    const handleExport = async () => {
        if (!chartContainerRef.current || isExporting) return;
        setIsExporting(true);
        try {
            const dataUrl = await toPng(chartContainerRef.current, {
                backgroundColor: '#ffffff',
                pixelRatio: 2,
                filter: (node) => !(node.dataset && node.dataset.exportIgnore === 'true'),
            });
            const link = document.createElement('a');
            link.download = `${sanitizeFilename(model.title)}.png`;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Chart export failed:', err);
        } finally {
            setIsExporting(false);
        }
    };

    if (!chartConfig?.content) return null;

    return (
        <>
            <div className="w-full p-6 bg-white rounded-lg shadow-md" ref={chartContainerRef} data-chart-card="true">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <h2 className="text-[20px] font-bold text-gray-800 leading-7" style={{ fontFamily: titleFont }}>
                        {model.title}
                    </h2>
                    <button
                        type="button"
                        onClick={handleExport}
                        disabled={isExporting}
                        className="flex items-center flex-none"
                        title="Download as PNG"
                        data-export-ignore="true"
                    >
                        <img src={png} alt="" className="w-6 h-6" />
                    </button>
                </div>
                <div ref={bodyRef} className="flex flex-col gap-6">
                    {model.empty ? (
                        <div className="text-gray-500 text-center p-8" style={{ fontFamily: FONT_FAMILY }}>No data available</div>
                    ) : model.panels.map((panel, index) => (
                        <Panel key={`${panel.kind}-${index}`} panel={panel} width={width} />
                    ))}
                </div>
            </div>
            <br />
        </>
    );
};

export default DynamicRechartsChart;
